import {
    IPhotoPickerStrings,
    StandalonePhotoPicker,
    UploadButtonProps
} from "@1js/photo-picker";
import { TwoUIProgressViewOperationData } from "@amx/ux-twoui-telemetry";
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { ShimmerCircle } from "../Shimmer/ShimmerCircle/ShimmerCircle";
import { ShimmerProgressView } from "../Shimmer/ShimmerProgressView/ShimmerProgressView";
import { Stack } from "../Stacks/Stack/Stack";

// photo picker did not export this type
// Please delete once they've exposed this type.
export type PictureEditorRef = {
    readonly startUploadNewPhoto: () => void;
};

/**
 * List of strings for when the photo is moved with the arrow keys.
 */
export type ArrowKeyMovementStrings = {
    readonly upArrowKeyAriaLabel: string;
    readonly downArrowKeyAriaLabel: string;
    readonly leftArrowKeyAriaLabel: string;
    readonly rightArrowKeyAriaLabel: string;
};

/**
 * List of strings for the photo pictures
 */
export type PhotoPickerStrings = IPhotoPickerStrings;

export interface PictureEditorProps {
    /**
     * Information about the monitored operation.
     */
    readonly operationData: TwoUIProgressViewOperationData;

    /**
     * Name of the user to display
     */
    readonly personDisplayName: string;

    /**
     * Image url
     */
    readonly personImageUrl: string;

    /**
     * Function to be called when image is uploaded
     */
    readonly onPictureSelected?: () => void;

    /**
     * Function to be called when canvas (the image cropper) is updated
     */
    readonly onCanvasUpdated: ((canvas: HTMLCanvasElement, isCanvasDirty?: boolean) => void) | undefined;

    /**
     * Function to inject photo picker strings to improve user experience.
     */
    readonly getPhotoPickerStrings: PhotoPickerStrings;

    /**
     * aria-label to be read out when a photo is selected. This is handled separately from
     * getPhotoPickerStrings because the Photo Picker requires a function.
     */
    readonly photoSelectedString: string;

    /**
     * aria-label to be read out when a when a photo is moved using the arrow keys.
     */
    readonly arrowKeyMovementStrings: ArrowKeyMovementStrings;
}

export const PictureEditor = forwardRef<PictureEditorRef, PictureEditorProps>(({
    operationData,
    personDisplayName,
    personImageUrl,
    onPictureSelected,
    onCanvasUpdated,
    getPhotoPickerStrings,
    photoSelectedString,
    arrowKeyMovementStrings
}, ref) => {
    const onFileSelected = useRef((_: File, __: boolean) => { return; });
    const fileInput = useRef<HTMLInputElement | null>(null);

    function getArrowKeyAriaLabel(label: string, p1: string, p2: string) {
        return label.replace("{0}", p1).replace("{1}", p2);
    }

    const getPhotoPickerStringsWithSelectedFunction: PhotoPickerStrings = {
        ...getPhotoPickerStrings,
        // Ensure that photoSelected is a function that takes a file name.
        photoSelected: (placeholders: { p0: string }) => {
            return photoSelectedString?.replace("{0}", placeholders.p0);
        },
        leftArrowKeyAriaLabel: ({ p0, p1 }) => getArrowKeyAriaLabel(arrowKeyMovementStrings.leftArrowKeyAriaLabel, p0, p1),
        rightArrowKeyAriaLabel: ({p0, p1}) => getArrowKeyAriaLabel(arrowKeyMovementStrings.rightArrowKeyAriaLabel, p0, p1),
        upArrowKeyAriaLabel: ({p0, p1}) => getArrowKeyAriaLabel(arrowKeyMovementStrings.upArrowKeyAriaLabel, p0, p1),
        downArrowKeyAriaLabel: ({p0, p1}) => getArrowKeyAriaLabel(arrowKeyMovementStrings.downArrowKeyAriaLabel, p0, p1),
        // Ensure that the user's name is put into the string for an existing picture.
        dropZoneImageAriaLabel: getPhotoPickerStrings.dropZoneImageAriaLabel?.replace("{0}", personDisplayName),
    };

    useImperativeHandle(ref, () => ({
        startUploadNewPhoto: () => {
            fileInput.current?.click();
        }
    }));

    const noOps = () => { return; };

    const onCanvasUpdatedInternal = (canvas: HTMLCanvasElement, isCanvasDirty?: boolean): void => {
        onCanvasUpdated?.(canvas, isCanvasDirty);
    };

    const shimmer = <ShimmerCircle height={256} />;

    const imageCropper = (
        <>
            <input
                role="button"
                style={{ display: "none" }}
                type="file"
                ref={fileInput}
                onClick={(ev) => (ev.currentTarget.value = "")}
                onChange={(ev) => {
                    if (ev.currentTarget.files) {
                        onFileSelected.current(ev.currentTarget.files[0], false);
                        onPictureSelected?.();
                    }
                }}
                accept="image/*"
            />
            <Stack horizontalAlign="center">
                <StandalonePhotoPicker
                    enableImageRotation={true}
                    personDisplayName={personDisplayName}
                    personImageUrl={personImageUrl}
                    strings={getPhotoPickerStringsWithSelectedFunction}
                    onUpload={noOps} /** only with build in upload button */
                    onDismiss={noOps} /** only with build in modal */
                    isImageRemovable={false}
                    isComponentInline={true}
                    imageCropShape={"Circle"}
                    imageScaling={"Fill"}
                    onCanvasUpdated={onCanvasUpdatedInternal}
                    hideButtons={true}
                    hideUploadHeader={true}
                    hideUploadPhotoButton={true}
                    renderCustomUploadButton={(photoPickerActions: UploadButtonProps): React.ReactElement<{}> => {
                        onFileSelected.current = photoPickerActions.onFileSelected;
                        return (<></>);
                    }}
                />
            </Stack>
        </>
    );

    return (
        <Stack
            verticalAlign="center"
            horizontalAlign="center"
            height={264}
            width={264}
            margin={"0 0 54px 0"}
        >
            <ShimmerProgressView
                operationData={operationData}
                firstLoadView={shimmer}
                updatingView={shimmer}
                finishedView={imageCropper}
                failedView={imageCropper}
            />
        </Stack>
    );
});
